import clsx from "clsx";
import { useMemo, type KeyboardEvent } from "react";
import type { TaskActionItem } from "@/services/api/agentic-v2";
import { formatRelativeTime } from "@/components/missions/MissionRow";
import { taskCardDescription } from "@/components/missions/taskDisplay";
import WorkerAvatar from "@/components/worker/WorkerAvatar";
import {
  WORKBENCH_CARD_SHELL_INTERACTIVE_CLASS,
  WORKBENCH_CARD_SHELL_SELECTED_CLASS,
} from "@/components/ui/workbench";

/** Kanban columns for client-scoped tasks (read-only grouping by status). */
const COLUMNS: { id: string; title: string; color: string; statuses: string[] }[] = [
  {
    id: "queued",
    title: "Queued",
    color: "#3b82f6",
    statuses: ["queued", "assigned"],
  },
  {
    id: "working",
    title: "Working",
    color: "#6366f1",
    statuses: ["in_progress", "working", "running"],
  },
  {
    id: "review",
    title: "Needs review",
    color: "#f59e0b",
    statuses: ["needs_review"],
  },
  {
    id: "blocked",
    title: "Blocked",
    color: "#ef4444",
    statuses: ["blocked", "gated"],
  },
  {
    id: "done",
    title: "Done",
    color: "#22c55e",
    statuses: ["done", "completed", "auto_closed"],
  },
];

export interface ClientTasksKanbanProps {
  tasks: TaskActionItem[];
  selectedTaskId: string | null;
  onSelectTask: (id: string) => void;
}

/** Group tasks into status columns; click opens the task thread panel. */
export function ClientTasksKanban({
  tasks,
  selectedTaskId,
  onSelectTask,
}: ClientTasksKanbanProps) {
  const columns = useMemo(() => groupTasksByColumn(tasks), [tasks]);

  return (
    <div
      className="flex flex-1 min-h-0 gap-3 overflow-x-auto pb-2"
      data-testid="client-tasks-kanban"
    >
      {columns.map((col) => (
        <KanbanColumn
          key={col.id}
          title={col.title}
          color={col.color}
          tasks={col.tasks}
          selectedTaskId={selectedTaskId}
          onSelectTask={onSelectTask}
        />
      ))}
    </div>
  );
}

function groupTasksByColumn(tasks: TaskActionItem[]) {
  const buckets = COLUMNS.map((col) => ({
    ...col,
    tasks: [] as TaskActionItem[],
  }));
  const other: TaskActionItem[] = [];

  for (const task of tasks) {
    const status = task.status.toLowerCase();
    const col = buckets.find((b) => b.statuses.includes(status));
    if (col) {
      col.tasks.push(task);
    } else {
      other.push(task);
    }
  }

  if (other.length > 0) {
    buckets.push({
      id: "other",
      title: "Other",
      color: "#94a3b8",
      statuses: [],
      tasks: other,
    });
  }

  return buckets;
}

function KanbanColumn({
  title,
  color,
  tasks,
  selectedTaskId,
  onSelectTask,
}: {
  title: string;
  color: string;
  tasks: TaskActionItem[];
  selectedTaskId: string | null;
  onSelectTask: (id: string) => void;
}) {
  return (
    <div
      className="flex min-h-0 min-w-[260px] max-w-[300px] flex-1 flex-col rounded-xl border border-slate-200 bg-slate-100/70 dark:border-white/10 dark:bg-white/[0.03]"
      data-testid={`kanban-column-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="px-3 py-2.5 border-b border-slate-200 dark:border-white/5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="h-2 w-2 rounded-full shrink-0"
            style={{ backgroundColor: color }}
            aria-hidden
          />
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-600 dark:text-slate-400 truncate">
            {title}
          </span>
        </div>
        <span className="text-[11px] text-slate-500 dark:text-slate-400 tabular-nums">{tasks.length}</span>
      </div>

      <ul className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2">
        {tasks.length === 0 ? (
          <li className="text-[11px] text-slate-400 dark:text-slate-600 px-2 py-4 text-center">—</li>
        ) : (
          tasks.map((task) => (
            <li key={task._id}>
              <TaskKanbanCard
                task={task}
                selected={task._id === selectedTaskId}
                onSelect={() => onSelectTask(task._id)}
              />
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

/** Narrow-column card: status comes from the column header, not a badge. */
function TaskKanbanCard({
  task,
  selected,
  onSelect,
}: {
  task: TaskActionItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect();
    }
  };

  const description = taskCardDescription(task);

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`kanban-task-${task._id}`}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className={clsx(
        "w-full rounded-xl px-3 py-2.5 text-left transition-colors cursor-pointer focus:outline-none",
        "bg-white ring-1 ring-slate-200 dark:bg-transparent dark:ring-0",
        WORKBENCH_CARD_SHELL_INTERACTIVE_CLASS,
        selected && WORKBENCH_CARD_SHELL_SELECTED_CLASS,
      )}
    >
      <div className="flex items-start gap-2">
        {task.agent ? (
          <WorkerAvatar
            seed={task.agent.name}
            size={24}
            user
            className="mt-0.5 shrink-0"
          />
        ) : null}

        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-start justify-between gap-2">
            <p className="min-w-0 flex-1 text-xs font-medium text-slate-900 dark:text-slate-100 line-clamp-2">
              {task.name}
            </p>
            <time
              className="shrink-0 text-[10px] text-slate-500 tabular-nums whitespace-nowrap pt-0.5"
              dateTime={task.updated_at}
              title={new Date(task.updated_at).toISOString()}
            >
              {formatRelativeTime(task.updated_at)}
            </time>
          </div>

          {task.mission?.name ? (
            <p className="text-[11px] text-slate-600 dark:text-slate-400 truncate">{task.mission.name}</p>
          ) : null}

          {description ? (
            <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2">{description}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
